import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'localization';
import type { SphereAttachment } from 'types/attachment';
import AttachmentRowAtom from '../../atoms/AttachmentRow/AttachmentRowAtom';
import './AttachmentListMolecule.scss';

export interface AttachmentListMoleculeProps {
  attachments: SphereAttachment[];
  sphereId: string;
  canEdit?: boolean;
  onDelete?: (attachment: SphereAttachment) => void | Promise<void>;
  onItemClick?: (attachment: SphereAttachment) => void;
  uploadedByNames?: Record<string, string>;
  /**
   * Called on an adaptive poll cycle whenever any row's `scan_status` is
   * `'pending'`. Parents should refetch metadata so rows can transition out of
   * the scanning state. Polling starts at 10s and backs off exponentially up
   * to 60s; it stops automatically when no pending rows remain.
   */
  onPollRefresh?: () => void | Promise<void>;
  className?: string;
}

const INITIAL_POLL_MS = 10_000;
const MAX_POLL_MS = 60_000;

function hasPending(attachments: SphereAttachment[]): boolean {
  for (const att of attachments) {
    if (att.scan_status === 'pending') return true;
  }
  return false;
}

function pendingSignature(attachments: SphereAttachment[]): string {
  const ids: string[] = [];
  for (const att of attachments) {
    if (att.scan_status === 'pending') ids.push(att.id);
  }
  ids.sort((a, b) => a.localeCompare(b));
  return ids.join(',');
}

const AttachmentListMolecule: React.FC<AttachmentListMoleculeProps> = ({
  attachments,
  sphereId,
  canEdit = false,
  onDelete,
  onItemClick,
  uploadedByNames,
  onPollRefresh,
  className = '',
}) => {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<SphereAttachment | null>(null);

  const deleteEnabled = canEdit && typeof onDelete === 'function';
  const signature = pendingSignature(attachments);
  const anyPending = hasPending(attachments);

  // Keep the latest callback in a ref so a stable `useEffect` dependency list
  // doesn't restart the timer every render when the parent passes an inline
  // function.
  const refreshRef = useRef(onPollRefresh);
  useEffect(() => {
    refreshRef.current = onPollRefresh;
  }, [onPollRefresh]);

  useEffect(() => {
    if (!anyPending || !refreshRef.current) return undefined;
    let cancelled = false;
    let interval = INITIAL_POLL_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = (): void => {
      if (cancelled) return;
      // Sync throws and async rejections both swallowed — parent is expected
      // to handle its own fetch errors and reflect them in the data we
      // re-render from. onPollRefresh is typed `void | Promise<void>`.
      try {
        const result = refreshRef.current?.();
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch(() => undefined);
        }
      } catch {
        // Swallow — parent handles fetch errors via the data it returns.
      }
      interval = Math.min(interval * 2, MAX_POLL_MS);
      timer = setTimeout(tick, interval);
    };

    timer = setTimeout(tick, interval);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `signature` restarts the poll with a fresh interval when the pending
    // set changes (new upload, one resolved, etc.).
  }, [anyPending, signature]);

  const handleRequestDelete = useCallback((attachment: SphereAttachment) => {
    setPendingDelete(attachment);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete || !onDelete) return;
    try {
      await onDelete(pendingDelete);
    } finally {
      setPendingDelete(null);
    }
  }, [pendingDelete, onDelete]);

  const rootClasses = ['attachment-list-molecule', className].filter(Boolean).join(' ');

  if (attachments.length === 0) {
    const emptyMessage = canEdit
      ? t('attachments.empty')
      : t('attachments.emptyReadOnly');
    return (
      <div className={rootClasses} data-sphere-id={sphereId} data-empty="true">
        <output className="attachment-list-molecule__empty">
          <i className="pi pi-paperclip" aria-hidden="true" />
          <span>{emptyMessage}</span>
        </output>
      </div>
    );
  }

  const confirmMessage = pendingDelete
    ? t('attachments.confirmDelete.message', {
        name: pendingDelete.name?.trim() || t('attachments.unnamed'),
      })
    : '';

  return (
    <div className={rootClasses} data-sphere-id={sphereId}>
      <ul className="attachment-list-molecule__list">
        {attachments.map((attachment) => (
          <li key={attachment.id} className="attachment-list-molecule__item">
            <AttachmentRowAtom
              attachment={attachment}
              uploadedByName={uploadedByNames?.[attachment.uploaded_by]}
              canDelete={deleteEnabled}
              onDelete={deleteEnabled ? handleRequestDelete : undefined}
              onClick={onItemClick}
            />
          </li>
        ))}
      </ul>
      <ConfirmDialog
        visible={pendingDelete !== null}
        onHide={handleCancelDelete}
        header={t('attachments.confirmDelete.title')}
        message={confirmMessage}
        icon="pi pi-exclamation-triangle"
        acceptLabel={t('attachments.confirmDelete.confirm')}
        rejectLabel={t('attachments.confirmDelete.cancel')}
        acceptClassName="p-button-danger"
        accept={handleConfirmDelete}
        reject={handleCancelDelete}
      />
    </div>
  );
};

export default AttachmentListMolecule;
